/**
 * Operation Confirmation Dialog Component
 * 
 * Unified modal dialog for confirming route operations (upload, download, delete) with safety checks and options.
 * Replaces separate consent checkboxes with a consistent, clear confirmation flow.
 * 
 * @module OperationConfirmationDialog
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  LinearProgress,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import {
  CloudUpload as UploadIcon,
  CloudDownload as DownloadIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  WarningAmberOutlined,
  DirectionsBus,
  CalendarMonth,
  WorkOutline,
} from '@mui/icons-material';
import type { Environment, UATRoute } from '../../../../services/fixedRouteUatClient';
import { getEnvironmentDisplayName, isProductionEnvironment } from '../EnvironmentSelector';
import type { FixedRoute } from '../../../../types/fixedRoutes';
import {
  describeServiceDays,
  expandDraftToPreview,
  collapsePreviewRows,
} from '../../../fixedRoutes/attachOperations/draftPlan';
import type { UploadPreview } from '../../../../hooks/useUploadRoutes';
import type { UploadShiftAssignmentMode } from '../../../../services/routeUploadShiftAssignment';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../../utils/iconFallbacks';
/**
 * Operation type
 */
export type OperationType = 'upload' | 'download' | 'delete';

// This dialog is launched from the Data Transfer drawer (DataTransferDrawer.tsx),
// whose Paper is hoisted to a custom zIndex of 2201 (with sub-layers up to ~2500).
// The Dialog itself is raised to `theme.zIndex.modal + 1300` (= 2600) so the whole
// dialog clears the drawer. MUI Select/Menu popups default to `theme.zIndex.modal`
// (1300), which would render them BEHIND the dialog and make them invisible. Raise
// every nested Select menu ONE step further (modal + 1400 = 2700) so it sits above
// the dialog. Mirrors the convention in fixedRoutes/AttachOperationsModal.tsx; both
// values must clear the drawer if either ever changes.
const POPPER_SX = { zIndex: (t: Theme) => t.zIndex.modal + 1400 };

/**
 * Props for OperationConfirmationDialog component
 */
export interface OperationConfirmationDialogProps {
  /** Operation type */
  operationType: OperationType;
  /** Whether dialog is open */
  open: boolean;
  /** Callback when dialog is closed */
  onClose: () => void;
  /** Routes to be operated on (for upload/download, these are the selected routes) */
  routes: Array<UATRoute | FixedRoute>;
  /** Selected environment */
  environment: Environment;
  /** Whether operation is in progress */
  isProcessing?: boolean;
  /** Operation progress (0-100) */
  progress?: number;
  /** Current route being processed */
  currentRoute?: string;
  /** Callback when operation is confirmed */
  onConfirm: (options?: {
    deleteUnconnectedStops?: boolean;
    applyToShifts?: boolean;
    shiftAssignmentMode?: UploadShiftAssignmentMode;
    operationSolutionId?: string;
  }) => void;
  /** Additional options specific to operation type */
  options?: {
    /** For delete: option to delete unconnected stops */
    deleteUnconnectedStops?: boolean;
  };
  /**
   * For upload only: classification of selected routes into overwrite vs create,
   * surfaced as a per-route breakdown in the dialog body. Computed by
   * `useUploadRoutes.previewUpload`.
   */
  uploadPreview?: UploadPreview | null;
  /** True while the preview is being fetched (disables Confirm). */
  uploadPreviewLoading?: boolean;
  /** Available operations solutions for the selected service date/timezone. */
  operationSolutions?: Array<{
    id: string;
    name: string;
    startDate?: string;
    endDate?: string;
  }>;
  operationSolutionsLoading?: boolean;
  operationSolutionsError?: string | null;
  shiftAssignmentDefaults?: {
    mode: UploadShiftAssignmentMode;
    operationSolutionId?: string;
  };
}

/**
 * OperationConfirmationDialog Component
 * 
 * Unified modal dialog for confirming route operations with safety checks.
 */
export const OperationConfirmationDialog: React.FC<OperationConfirmationDialogProps> = ({
  operationType,
  open,
  onClose,
  routes,
  environment,
  isProcessing = false,
  progress = 0,
  currentRoute,
  onConfirm,
  options,
  uploadPreview,
  uploadPreviewLoading = false,
  operationSolutions = [],
  operationSolutionsLoading = false,
  operationSolutionsError = null,
  shiftAssignmentDefaults,
}) => {
  const [deleteUnconnectedStops, setDeleteUnconnectedStops] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [overwriteAcknowledged, setOverwriteAcknowledged] = useState(false);
  const [applyToShifts, setApplyToShifts] = useState(true);
  const [shiftAssignmentMode, setShiftAssignmentMode] = useState<UploadShiftAssignmentMode>('warn-only');
  const [selectedOperationSolutionId, setSelectedOperationSolutionId] = useState('');
  const [productionConfirmationText, setProductionConfirmationText] = useState('');
  // Phase C: explicit acceptance that a UAT upload will create shift solutions in
  // Ops (live UAT write). Gates Confirm; see uatShiftCreationGated below.
  const [shiftCreationAcknowledged, setShiftCreationAcknowledged] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setDeleteUnconnectedStops(false);
      setAcknowledged(false);
      setOverwriteAcknowledged(false);
      setApplyToShifts(true);
      setShiftAssignmentMode(shiftAssignmentDefaults?.mode ?? 'warn-only');
      setSelectedOperationSolutionId(shiftAssignmentDefaults?.operationSolutionId || '');
      setProductionConfirmationText('');
      setShiftCreationAcknowledged(false);
    }
  }, [open, shiftAssignmentDefaults?.mode, shiftAssignmentDefaults?.operationSolutionId]);

  const overwriteCount = uploadPreview?.willOverwrite.length ?? 0;
  const createCount = uploadPreview?.willCreate.length ?? 0;
  const needsOverwriteAck = operationType === 'upload' && overwriteCount > 0;

  // ─── Phase C: UAT shift-creation acceptance gate ──────────────────────────────
  // UAT is the only environment where shift creation runs (live write). PROD stays
  // gated: shifts are NOT created, so no acceptance is required there.
  const isUatEnvironment = environment === 'UAT' || environment === 'EUWE2_UAT';

  // The selected Atlas routes carrying an assigned plan (operationAssignments).
  const planRoutes = routes.filter(
    (route): route is FixedRoute =>
      'operationAssignments' in route && !!route.operationAssignments?.length,
  );

  // Count of distinct shift solutions that WILL be created from the selected
  // routes' operationAssignments — one per (operation × vehicle × shift), with the
  // per-day preview rows collapsed (mirrors the Hailer model; see draftPlan.ts).
  const shiftSolutionCount = planRoutes.reduce(
    (sum, route) => sum + collapsePreviewRows(expandDraftToPreview(route)).length,
    0,
  );

  // Effective shift-assignment mode in the dialog (user-overridable state).
  const willCreateDatedShifts = operationType === 'upload' && shiftAssignmentMode === 'create-dated-shifts';

  // A confirm-blocking acceptance is required only when this UAT upload WILL create
  // shift solutions (create-dated-shifts mode + at least one shift solution).
  const uatShiftCreationGated =
    willCreateDatedShifts && isUatEnvironment && shiftSolutionCount > 0;

  // PROD note: create-dated-shifts was selected but shifts won't be created in PROD.
  const showProdShiftCreationNote =
    willCreateDatedShifts && !isUatEnvironment && isProductionEnvironment(environment) && shiftSolutionCount > 0;

  const handleConfirm = () => {
    if (!acknowledged) return;
    if (needsOverwriteAck && !overwriteAcknowledged) return;
    if (uatShiftCreationGated && !shiftCreationAcknowledged) return;
    if (operationType === 'delete') {
      onConfirm({ deleteUnconnectedStops });
    } else if (operationType === 'upload') {
      onConfirm({
        applyToShifts,
        shiftAssignmentMode,
        operationSolutionId: selectedOperationSolutionId || undefined,
      });
    } else {
      onConfirm();
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      onClose();
    }
  };

  const requiresProductionTypeConfirm = isProductionEnvironment(environment);
  const productionTypeConfirmed = !requiresProductionTypeConfirm || productionConfirmationText.trim().toUpperCase() === 'PROD';
  const requiresOperationSelection =
    operationType === 'upload' && createCount > 0 && shiftAssignmentMode === 'create-dated-shifts';
  const operationSelectionComplete = !requiresOperationSelection || !!selectedOperationSolutionId;

  const canProceed =
    acknowledged &&
    !isProcessing &&
    !uploadPreviewLoading &&
    productionTypeConfirmed &&
    operationSelectionComplete &&
    (!needsOverwriteAck || overwriteAcknowledged) &&
    (!uatShiftCreationGated || shiftCreationAcknowledged);

  // Calculate totals
  const totalStops = routes.reduce((sum, route) => {
    if ('stops' in route && Array.isArray(route.stops)) {
      return sum + route.stops.length;
    }
    if ('routeStops' in route && route.routeStops?.next) {
      return sum + route.routeStops.next.length;
    }
    return sum;
  }, 0);

  // Get operation-specific details
  const getOperationDetails = () => {
    switch (operationType) {
      case 'upload': {
        const previewSummary = uploadPreview
          ? `${overwriteCount} will get a new version; ${createCount} will be created.`
          : 'This will create new route versions or new routes in the database.';
        const createWarning =
          createCount > 0
            ? ' Newly created routes require shift assignment before they are operational.'
            : '';
        return {
          title: 'Confirm Route Upload',
          icon: <Icon size={20} name="upload" />,
          warningMessage: `The selected routes will be uploaded to ${getEnvironmentDisplayName(environment)}. ${previewSummary}${createWarning}`,
          actionButtonText: 'Upload Routes',
          actionButtonIcon: <Icon size={20} name="upload" />,
          acknowledgmentText: `I acknowledge that I am responsible for uploading these routes to ${getEnvironmentDisplayName(environment)}`,
        };
      }
      case 'download':
        return {
          title: 'Confirm Route Download',
          icon: <Icon size={20} d={ICON_FALLBACKS.CloudDownload} />,
          warningMessage: `The selected routes will be downloaded from ${getEnvironmentDisplayName(environment)} and imported into Atlas. Existing routes with the same name may be updated.`,
          actionButtonText: 'Download Routes',
          actionButtonIcon: <Icon size={20} d={ICON_FALLBACKS.CloudDownload} />,
          acknowledgmentText: `I acknowledge that I am responsible for downloading these routes from ${getEnvironmentDisplayName(environment)} to Atlas`,
        };
      case 'delete':
        return {
          title: 'Confirm Route Deletion',
          icon: <Icon size={20} name="trash" />,
          warningMessage: `This action cannot be undone. The selected routes will be permanently deleted from ${getEnvironmentDisplayName(environment)}.`,
          actionButtonText: 'Delete Routes',
          actionButtonIcon: <Icon size={20} name="trash" />,
          acknowledgmentText: `I acknowledge that I am responsible for deleting these routes from ${getEnvironmentDisplayName(environment)}`,
        };
    }
  };

  const details = getOperationDetails();

  // Get route name helper
  const getRouteName = (route: UATRoute | FixedRoute): string => {
    // UATRoute has 'label' property
    if ('label' in route && route.label) {
      return route.label;
    }
    // FixedRoute has 'name' property
    if ('name' in route && route.name) {
      return route.name;
    }
    // Both have 'id' property as fallback
    return route.id;
  };

  // Get route ID helper
  const getRouteId = (route: UATRoute | FixedRoute): string => {
    // Both types have 'id' property
    return route.id;
  };

  // Get stop count helper
  const getStopCount = (route: UATRoute | FixedRoute): number => {
    if ('stops' in route && Array.isArray(route.stops)) {
      return route.stops.length;
    }
    if ('routeStops' in route && route.routeStops?.next) {
      return route.routeStops.next.length;
    }
    return 0;
  };

  // Atlas FixedRoutes carrying an assigned plan (operationAssignments),
  // composed in the Attach Vehicles & Operations modal. Read-only summary only.
  // Same set used by the UAT shift-creation gate above (see planRoutes).
  const routesWithPlan = planRoutes;

  // Compact, read-only "Vehicles & Operations" readout for a single route.
  // Mirrors the RouteCard readout pattern (describeServiceDays + MUI icons).
  const renderRoutePlan = (route: FixedRoute) => (
    <Box key={route.id} sx={{ mb: 1.5 }}>
      <Typography variant="body2" fontWeight="bold">
        {getRouteName(route)}
      </Typography>
      {(route.operationAssignments ?? []).map((op, opIdx) => (
        <Box key={op.operationSolutionId || opIdx} sx={{ mt: 0.5, ml: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <WorkOutline fontSize="inherit" />
            {op.operationName || op.operationSolutionId || 'Operation (assign at upload)'}
            {op.dateRange ? ` · ${op.dateRange.startDate} → ${op.dateRange.endDate}` : ''}
          </Typography>
          <List dense disablePadding>
            {op.vehicleAssignments.map((va, vi) => (
              <ListItem key={`${va.opsVehicleId}-${vi}`} sx={{ px: 1, py: 0.25 }}>
                <ListItemText
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption', component: 'div' }}
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <DirectionsBus fontSize="inherit" />
                      {va.opsVehicleLabel || va.opsVehicleId || 'Unmapped vehicle'}
                    </Box>
                  }
                  secondary={
                    <>
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarMonth fontSize="inherit" />
                        {va.serviceDays ? describeServiceDays(va.serviceDays) : '—'}
                        {` · ${va.startTime || '—'}–${va.endTime || '—'}`}
                      </Box>
                      <br />
                      {`${va.direction} · ${va.shiftType}`}
                      {va.numberOfRepeats > 0 ? ` · ${va.numberOfRepeats} repeats` : ''}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      ))}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={isProcessing}
      // Raised above the Data Transfer drawer (2201 + sub-layers); see POPPER_SX.
      sx={{ zIndex: (t) => t.zIndex.modal + 1300 }}
      data-testid="transfer.confirm-dialog.root"
      PaperProps={{
        sx: {
          minHeight: '400px',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon size={20} name="triangle-exclamation" />
          <Typography variant="h6">{details.title}</Typography>
          <Chip
            label={getEnvironmentDisplayName(environment)}
            size="small"
            color={environment === 'UAT' ? 'success' : 'primary'}
            sx={{ ml: 'auto' }}
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Warning Alert */}
        <Alert 
          severity={operationType === 'delete' ? 'error' : 'warning'} 
          sx={{ mb: 2 }}
        >
          <Typography variant="body2" fontWeight="bold">
            {operationType === 'delete' 
              ? 'This action cannot be undone.'
              : 'Please review the details below before proceeding.'}
          </Typography>
          <Typography variant="body2">
            {details.warningMessage}
          </Typography>
        </Alert>

        {/* Operation Summary */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            You are about to {operationType}:
          </Typography>
          <Typography variant="h6" color={operationType === 'delete' ? 'error' : 'primary'}>
            {routes.length} route{routes.length === 1 ? '' : 's'}
          </Typography>
          {totalStops > 0 && (
            <Typography variant="body2" color="text.secondary">
              {totalStops} total stop{totalStops === 1 ? '' : 's'}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Route List */}
        {operationType === 'upload' && uploadPreviewLoading ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Checking which routes already exist on {getEnvironmentDisplayName(environment)}…
            </Typography>
            <LinearProgress />
          </Box>
        ) : operationType === 'upload' && uploadPreview ? (
          <Box
            sx={{
              mb: 2,
              maxHeight: '400px',
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              backgroundColor: 'background.default',
              p: 1,
            }}
          >
            {overwriteCount > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2">
                  {overwriteCount} route{overwriteCount === 1 ? '' : 's'} will get a new version
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Existing shifts and drivers stay attached.
                </Typography>
                <List dense>
                  {uploadPreview.willOverwrite.map((r) => (
                    <ListItem key={r.routeId}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" component="span">
                              {r.name}
                            </Typography>
                            <Chip
                              label={r.routeVersionName}
                              size="small"
                              variant="outlined"
                              color="info"
                            />
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            → {r.nextVersionName}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
            {createCount > 0 && (
              <Box>
                <Typography variant="subtitle2">
                  {createCount} route{createCount === 1 ? '' : 's'} will be created
                </Typography>
                <List dense>
                  {uploadPreview.willCreate.map((r) => (
                    <ListItem key={r.routeId}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" component="span">
                              {r.name}
                            </Typography>
                            <Chip
                              label={r.routeVersionName}
                              size="small"
                              variant="outlined"
                              color="default"
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              mb: 2,
              maxHeight: '400px',
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              backgroundColor: 'background.default',
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Routes to be {operationType === 'upload' ? 'uploaded' : operationType === 'download' ? 'downloaded' : 'deleted'}:
            </Typography>
            <List dense>
              {routes.map((route, index) => (
                <ListItem key={`${getRouteId(route)}-${index}`}>
                  <ListItemText
                    primary={getRouteName(route)}
                    secondary={
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          ID: {getRouteId(route).length > 20
                            ? `${getRouteId(route).substring(0, 20)}...`
                            : getRouteId(route)}
                        </Typography>
                        {getStopCount(route) > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            • {getStopCount(route)} stop{getStopCount(route) === 1 ? '' : 's'}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Assigned plan (Vehicles & Operations) — read-only summary of the plan
            carried on the selected Atlas FixedRoutes. Rendered only when at
            least one route being uploaded has operationAssignments. */}
        {routesWithPlan.length > 0 && (
          <Box
            sx={{
              mb: 2,
              maxHeight: '320px',
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              backgroundColor: 'background.default',
              p: 1,
            }}
          >
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <DirectionsBus fontSize="small" />
              Vehicles & Operations
            </Typography>
            {routesWithPlan.map((route) => renderRoutePlan(route))}
          </Box>
        )}

        {/* Progress Indicator */}
        {isProcessing && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {currentRoute ? `${operationType === 'upload' ? 'Uploading' : operationType === 'download' ? 'Downloading' : 'Deleting'}: ${currentRoute}` : `${operationType === 'upload' ? 'Uploading' : operationType === 'download' ? 'Downloading' : 'Deleting'} routes...`}
            </Typography>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {Math.round(progress)}% complete
            </Typography>
          </Box>
        )}

        {/* Delete-specific: Unconnected Stops Option */}
        {operationType === 'delete' && (
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={deleteUnconnectedStops}
                  onChange={(e) => setDeleteUnconnectedStops(e.target.checked)}
                  disabled={isProcessing}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Delete unconnected stops</Typography>
                  <Typography variant="caption" color="text.secondary">
                    If checked, stops that are not used by any other routes will also be deleted.
                  </Typography>
                </Box>
              }
            />
          </Box>
        )}

        {/* Upload-specific: Apply new versions to future shifts */}
        {operationType === 'upload' && overwriteCount > 0 && (
          <Box sx={{ mb: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={applyToShifts}
                  onChange={(e) => setApplyToShifts(e.target.checked)}
                  disabled={isProcessing}
                  inputProps={{ 'data-testid': 'transfer.confirm-dialog.apply-to-shifts' } as React.InputHTMLAttributes<HTMLInputElement>}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Apply new versions to future shifts</Typography>
                  <Typography variant="caption" color="text.secondary">
                    If checked, upcoming shifts on the overwritten routes will switch to the newly
                    created version. Uncheck to leave shifts on the previous version.
                  </Typography>
                </Box>
              }
            />
          </Box>
        )}

        {/* Upload-specific: Overwrite acknowledgment (additional safety check) */}
        {needsOverwriteAck && (
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={overwriteAcknowledged}
                  onChange={(e) => setOverwriteAcknowledged(e.target.checked)}
                  disabled={isProcessing}
                />
              }
              label={
                <Typography variant="body2">
                  I understand that {overwriteCount} existing route
                  {overwriteCount === 1 ? '' : 's'} will be updated with a new version.
                </Typography>
              }
            />
          </Box>
        )}

        {/* Upload-specific: New route shift assignment mode */}
        {operationType === 'upload' && createCount > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              New route shift assignment
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <InputLabel id="shift-assignment-mode-label">Assignment mode</InputLabel>
              <Select
                labelId="shift-assignment-mode-label"
                value={shiftAssignmentMode}
                label="Assignment mode"
                onChange={(event) =>
                  setShiftAssignmentMode(event.target.value as UploadShiftAssignmentMode)
                }
                disabled={isProcessing}
                // Raise above the dialog so the menu is visible (see POPPER_SX).
                MenuProps={{ sx: POPPER_SX }}
              >
                <MenuItem value="warn-only">
                  Warn only (no shift creation, manual Ops assignment required)
                </MenuItem>
                <MenuItem value="create-dated-shifts">
                  Create dated shift solutions after upload
                </MenuItem>
              </Select>
            </FormControl>

            {shiftAssignmentMode === 'warn-only' ? (
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                {createCount} new route{createCount === 1 ? '' : 's'} will require manual shift
                assignment in Ops.
              </Alert>
            ) : (
              <>
                <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                  <InputLabel id="operation-solution-select-label">Operation solution</InputLabel>
                  <Select
                    labelId="operation-solution-select-label"
                    value={selectedOperationSolutionId}
                    label="Operation solution"
                    onChange={(event) => setSelectedOperationSolutionId(event.target.value)}
                    disabled={isProcessing || operationSolutionsLoading}
                    // Raise above the dialog so the menu is visible (see POPPER_SX).
                    MenuProps={{ sx: POPPER_SX }}
                  >
                    {operationSolutions.map((operationSolution) => (
                      <MenuItem key={operationSolution.id} value={operationSolution.id}>
                        {operationSolution.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {operationSolutionsLoading && (
                  <Typography variant="caption" color="text.secondary">
                    Loading operations solutions for selected service date...
                  </Typography>
                )}
                {!operationSolutionsLoading && operationSolutions.length === 0 && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    No operations solutions available for the selected service date/timezone.
                  </Alert>
                )}
                {operationSolutionsError && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {operationSolutionsError}
                  </Alert>
                )}
              </>
            )}
          </Box>
        )}

        {/* Phase C: UAT shift-creation acceptance — REQUIRED before a UAT upload
            that will create shift solutions in Ops can proceed. */}
        {uatShiftCreationGated && (
          <Box sx={{ mt: 2 }} data-testid="transfer.confirm-dialog.uat-shift-ack-block">
            <Alert
              severity="warning"
              icon={<WarningAmberOutlined fontSize="inherit" />}
              sx={{ mb: 1 }}
            >
              <Typography variant="body2" fontWeight="bold">
                This upload will create {shiftSolutionCount} shift solution
                {shiftSolutionCount === 1 ? '' : 's'} in {getEnvironmentDisplayName(environment)} Ops.
              </Typography>
              <Typography variant="body2">
                Shift solutions are written live to UAT Operations using the vehicles and
                operation you selected. Confirm you want to proceed.
              </Typography>
            </Alert>
            <FormControlLabel
              control={
                <Checkbox
                  checked={shiftCreationAcknowledged}
                  onChange={(e) => setShiftCreationAcknowledged(e.target.checked)}
                  disabled={isProcessing}
                  inputProps={{ 'data-testid': 'transfer.confirm-dialog.uat-shift-ack' } as React.InputHTMLAttributes<HTMLInputElement>}
                />
              }
              label={
                <Typography variant="body2">
                  I understand this will create {shiftSolutionCount} shift solution
                  {shiftSolutionCount === 1 ? '' : 's'} in {getEnvironmentDisplayName(environment)} Ops.
                </Typography>
              }
            />
          </Box>
        )}

        {/* Phase C: PROD upload selected create-dated-shifts, but shift creation is
            UAT-only. Non-blocking note; PROD upload proceeds without shifts. */}
        {showProdShiftCreationNote && (
          <Box sx={{ mt: 2 }} data-testid="transfer.confirm-dialog.prod-shift-note">
            <Alert severity="info" icon={<WarningAmberOutlined fontSize="inherit" />}>
              <Typography variant="body2">
                Shift creation runs in UAT only. This {getEnvironmentDisplayName(environment)} upload
                will create the route versions but will not create shift solutions.
              </Typography>
            </Alert>
          </Box>
        )}

        {/* User Acknowledgment */}
        {requiresProductionTypeConfirm && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="error" sx={{ mb: 1.5 }}>
              Production mode is live. Use your manually configured Production JWT, then type
              {' '}<strong>PROD</strong>{' '}to confirm this action.
            </Alert>
            <TextField
              fullWidth
              size="small"
              label="Type PROD to confirm"
              value={productionConfirmationText}
              onChange={(event) => setProductionConfirmationText(event.target.value)}
              disabled={isProcessing}
              error={!productionTypeConfirmed && productionConfirmationText.length > 0}
              helperText={!productionTypeConfirmed && productionConfirmationText.length > 0 ? 'Enter PROD exactly to continue.' : ' '}
            />
          </Box>
        )}

        {/* User Acknowledgment */}
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                disabled={isProcessing}
                inputProps={{ 'data-testid': 'transfer.confirm-dialog.ack-checkbox' } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            }
            label={
              <Typography variant="body2">
                {details.acknowledgmentText}
              </Typography>
            }
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isProcessing}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={operationType === 'delete' ? 'error' : 'primary'}
          startIcon={details.actionButtonIcon}
          onClick={handleConfirm}
          disabled={!canProceed}
          data-testid="transfer.confirm-dialog.confirm"
        >
          {isProcessing 
            ? `${operationType === 'upload' ? 'Uploading' : operationType === 'download' ? 'Downloading' : 'Deleting'}...` 
            : details.actionButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
